/**
 * FormDetail — a single form's overview page in the member Forms
 * Library: "Complete online" (starts a new saved copy), "Print blank
 * form", and "My saved copies" (continue/view/print/delete existing
 * copies of this form, owned exclusively by the signed-in member).
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FilePlus2, Printer, Pencil, Trash2, CheckCircle2 } from "lucide-react";
import { api, getFriendlyErrorMessage } from "../lib/api";
import { PageHero } from "../components/shared/Primitives";
import HandoverGate from "../components/layout/HandoverGate";
import { RichTextDisplay } from "../components/forms/RichTextDisplay";

const ANY_MEMBER_ENTITLEMENT = ["carer_free", "carer_profile", "carer_essentials", "pro_carer", "family_free", "family_circle"];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function FormDetailInner() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    try {
      const [formRes, subsRes] = await Promise.all([
        api.get(`/api/forms/${templateId}`),
        api.get(`/api/forms/${templateId}/my-submissions`),
      ]);
      setForm(formRes.data);
      setSubmissions(subsRes.data.submissions || []);
    } catch (err) {
      setError(getFriendlyErrorMessage(err));
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, [templateId]);

  const startNewCopy = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/api/forms/${templateId}/submissions`, {});
      navigate(`/forms/${templateId}/copy/${data.submission_id}`);
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const deleteCopy = async (submissionId) => {
    try {
      await api.delete(`/api/forms/submissions/${submissionId}`);
      toast.success("Copy deleted.");
      setSubmissions((prev) => prev.filter((s) => s.submission_id !== submissionId));
    } catch (err) {
      toast.error(getFriendlyErrorMessage(err));
    }
  };

  if (!loaded) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="form-detail-loading">Loading…</div>;
  }
  if (error || !form) {
    return (
      <div className="container-jcc py-24 text-center" data-testid="form-detail-error">
        <p className="text-red-600 text-sm">{error || "Form not found."}</p>
        <Link to="/forms" className="btn-outline text-sm mt-6 inline-flex">Back to Forms Library</Link>
      </div>
    );
  }

  return (
    <div data-testid="form-detail-page">
      <PageHero eyebrow="JCC Forms" title={form.title}>
        <Link to="/forms" className="btn-outline text-sm" data-testid="form-detail-back-link">
          <ArrowLeft className="w-4 h-4" /> Forms Library
        </Link>
      </PageHero>

      <section className="section pt-8 pb-24">
        <div className="container-jcc max-w-3xl">
          <RichTextDisplay content={form.description} className="text-ink-muted mb-4" testId="form-detail-description" />
          {form.intro_text && (
            <div className="bg-slate-50 border border-slate-100 rounded-lg p-4 mb-6" data-testid="form-detail-intro">
              <RichTextDisplay content={form.intro_text} />
            </div>
          )}

          <div className="flex flex-wrap gap-3 mb-8">
            <button type="button" onClick={startNewCopy} disabled={starting} className="btn-teal text-sm" data-testid="form-detail-start-new-btn">
              <FilePlus2 className="w-4 h-4" /> {starting ? "Starting…" : "Complete online"}
            </button>
            <Link to={`/forms/${templateId}/print`} className="btn-outline text-sm" data-testid="form-detail-print-blank-btn">
              <Printer className="w-4 h-4" /> Print blank form
            </Link>
          </div>

          <h2 className="font-display text-lg font-semibold mb-3">My saved copies</h2>
          {submissions.length === 0 ? (
            <p className="text-ink-muted text-sm border border-dashed border-slate-200 rounded-xl p-6 text-center" data-testid="form-detail-no-copies">
              You haven't started a copy of this form yet.
            </p>
          ) : (
            <div className="space-y-2" data-testid="form-detail-copies-list">
              {submissions.map((s) => (
                <div key={s.submission_id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-4" data-testid={`form-detail-copy-${s.submission_id}`}>
                  <div>
                    <p className="font-medium text-ink flex items-center gap-1.5">
                      {s.copy_label}
                      {s.status === "completed" && <CheckCircle2 className="w-3.5 h-3.5 text-green-600" data-testid={`form-detail-copy-${s.submission_id}-completed-icon`} />}
                    </p>
                    <p className="text-xs text-ink-soft">
                      {s.status === "completed" ? "Completed" : "Draft"} · last saved {fmt(s.updated_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Link to={`/forms/${templateId}/copy/${s.submission_id}`} className="text-teal font-medium text-sm inline-flex items-center gap-1"
                          data-testid={`form-detail-copy-${s.submission_id}-open`}>
                      <Pencil className="w-3.5 h-3.5" /> {s.status === "completed" ? "View / edit" : "Continue"}
                    </Link>
                    <button type="button" onClick={() => deleteCopy(s.submission_id)} className="text-ink-soft hover:text-red-600"
                            data-testid={`form-detail-copy-${s.submission_id}-delete`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default function FormDetail() {
  return (
    <HandoverGate requiredEntitlement={ANY_MEMBER_ENTITLEMENT}>
      <FormDetailInner />
    </HandoverGate>
  );
}
